import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, useTheme, Divider } from '@ublend-npm/aulaui-core';
import { toggleStarSpace } from '@core/spaceSelector/operations';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { getHomeStarredSpaces } from '@app/selectors/home';

import { useResponsiveSpacesNavigation } from '@app/hooks/useResponsiveSpacesNavigation';

import SpaceListSkeleton from '../common/SpaceListSkeleton';
import StarredSpaceItem from './StarredSpaceItem';
import { getStarredSpaceColors } from '../../colors';
import StarredSpacesEmptyState from './StarredSpacesEmptyState';
import { WidgetCard } from '../common';
import SpacesListModal from '../../../AllSpaces/SpacesListModal';
import ResponsiveModalPageSwitcher from '../../../common/ResponsiveModalPageSwitcher';
import AllSpacesPage from '../../../AllSpaces/AllSpacesPage';

const STRINGS = {
  emptyState: 'No starred spaces.',
  title: 'Starred spaces',
  hide: 'Hide',
  viewAll: 'View all starred spaces',
  widgetKey: 'home-starred-spaces-widget',
  helpArticleUrl:
    'https://aula.zendesk.com/hc/en-us/articles/15852659347218--Starred-Spaces',
};

const ITEM_RENDER_COUNT = 5;

const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2'];

const StarredSpacesWidget = () => {
  const { palette } = useTheme();
  const dispatch = useDispatch<any>();
  const { isModalOpen, modalType, openSpacesList, closeAllSpacesModal } =
    useResponsiveSpacesNavigation();

  const isLoading = false; // Temporarily false for testing
  const rawSpaces = useSelector(getHomeStarredSpaces);

  const spaces = useMemo(() => {
    return rawSpaces.map((s: any) => ({
      id: s.objectId,
      name: s.name,
      startDate: s.startDate,
      endDate: s.endDate,
    }));
  }, [rawSpaces]);

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference(STRINGS.widgetKey, true));
  };

  const handleUnstar = useCallback(
    (id: string) => {
      dispatch(toggleStarSpace({ spaceId: id }));
    },
    [dispatch]
  );

  const content = useMemo(() => {
    if (isLoading) {
      return SKELETON_KEYS.map((key, index) => (
        <React.Fragment key={key}>
          <SpaceListSkeleton />
          {index < 2 && <Divider />}
        </React.Fragment>
      ));
    }

    if (spaces.length === 0) {
      return (
        <StarredSpacesEmptyState
          helpArticleUrl={STRINGS.helpArticleUrl}
          onTrigger={() => openSpacesList('all')}
        />
      );
    }

    const visibleSpaces = spaces.slice(0, ITEM_RENDER_COUNT);

    return visibleSpaces.map((space) => (
      <React.Fragment key={space.id}>
        <StarredSpaceItem space={space} onUnstar={handleUnstar} />
        <Divider />
      </React.Fragment>
    ));
  }, [isLoading, spaces, handleUnstar, openSpacesList]);

  const menuActions = [{ label: STRINGS.hide, onClick: handleHideWidget }];

  const footer = (
    <Button
      fullWidth
      kind="secondary"
      endIcon={<ArrowForwardIcon />}
      sx={{
        width: '100%',
        color: getStarredSpaceColors(palette).starIcon,
      }}
      onClick={() => openSpacesList('starred')}
    >
      {STRINGS.viewAll}
    </Button>
  );

  const footerUI = spaces.length === 0 ? null : footer;

  return (
    <>
      <WidgetCard
        title={STRINGS.title}
        menuActions={menuActions}
        footer={footerUI}
      >
        {content}
      </WidgetCard>

      <ResponsiveModalPageSwitcher
        isOpen={isModalOpen}
        onClose={closeAllSpacesModal}
        modalComponent={
          <SpacesListModal
            isOpen={isModalOpen}
            onClose={closeAllSpacesModal}
            type={modalType}
          />
        }
        pageComponent={<AllSpacesPage onClose={closeAllSpacesModal} />}
      />
    </>
  );
};

export default StarredSpacesWidget;
